import { UserProfileComponentProps } from '@components/pages/home/components/home-user-profile-modal.component.tsx';
import { CountrySelector } from '@components/shared/country-selector.component';
import { ErrorMessage } from '@components/shared/error-message.component';
import { FormLabel } from '@components/shared/form-label.component';
import { PopoverSelector } from '@components/shared/popover-selector.component';
import { Input } from '@components/ui/input';
import { cn } from '@lib/utils';
import { DEFAULT_COUNTRY_CODE, countryPhoneCodes } from '@utils/constants.ts';
import React, { FunctionComponent } from 'react';

type PersonalStepProps = UserProfileComponentProps;

export const PersonalStep: FunctionComponent<PersonalStepProps> = (props) => {
  const { errors, formData, handleChange, setFormData } = props;
  const defaultCountryCode = formData.countryCode || DEFAULT_COUNTRY_CODE;

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numericValue = e.target.value.replace(/[^0-9]/g, '');
    const parsedValue = numericValue ? parseInt(numericValue, 10) : '';

    setFormData((prev) => ({
      ...prev,
      [e.target.name]: parsedValue,
    }));
  };

  return (
    <div className="flex w-full flex-col items-start gap-6 p-3">
      <div className="flex w-full flex-row items-start gap-4">
        <div className="flex w-full flex-col items-start gap-1.5">
          <FormLabel
            label="First Name"
            isRequired
            htmlFor="firstName"
          />
          <Input
            data-1p-ignore
            autoComplete="off"
            id="firstName"
            name="firstName"
            type="text"
            value={formData.firstName}
            onChange={handleChange}
            required
            className={cn('flex h-10 w-full rounded-lg bg-gray-100 p-3', errors.firstName && 'border border-red-500')}
          />
          <ErrorMessage zodError={errors.firstName} />
        </div>
        <div className="flex w-full flex-col items-start gap-1.5">
          <FormLabel
            label="Last Name"
            isRequired
            htmlFor="lastName"
          />
          <Input
            data-1p-ignore
            autoComplete="off"
            id="lastName"
            name="lastName"
            type="text"
            value={formData.lastName}
            onChange={handleChange}
            required
            className={cn('flex h-10 w-full rounded-lg bg-gray-100 p-3', errors.lastName && 'border border-red-500')}
          />
          <ErrorMessage zodError={errors.lastName} />
        </div>
      </div>
      <div className="flex w-full flex-col items-start gap-1.5">
        <FormLabel
          label="Title"
          isRequired
          htmlFor="title"
        />
        <Input
          id="title"
          name="title"
          type="text"
          value={formData.title}
          onChange={handleChange}
          required
          className={cn('flex h-10 w-full rounded-lg bg-gray-100 p-3', errors.title && 'border border-red-500')}
        />
        <ErrorMessage zodError={errors.title} />
      </div>
      <CountrySelector
        error={errors.country?._errors?.[0]}
        isRequired={false}
        label="Citizenship"
        name="country"
        onChange={handleChange}
        placeholder="Select citizenship"
        value={formData.country}
      />
      <div className="flex w-full flex-col items-start gap-1.5">
        <FormLabel
          label="Phone Number"
          htmlFor="countryCode"
        />
        <div className={cn('flex w-full flex-row gap-1', errors.phoneNumber && 'border border-red-500')}>
          <PopoverSelector
            className="w-[100px]"
            contentClassName="max-w-[150px]"
            emptyMessage="No country code found"
            name="countryCode"
            onChange={handleChange}
            options={countryPhoneCodes.map((code) => ({ value: code, label: code }))}
            placeholder={defaultCountryCode}
            value={formData.countryCode}
          />
          <Input
            id="phoneNumber"
            name="phoneNumber"
            type="text"
            pattern="[0-9]*"
            inputMode="numeric"
            value={formData.phoneNumber}
            onChange={handlePhoneNumberChange}
            className="flex h-10 w-full rounded-lg bg-gray-100 p-3"
          />
        </div>
        <ErrorMessage zodError={errors.phoneNumber} />
      </div>
    </div>
  );
};
